import React, {useState} from 'react';
import PropTypes from 'prop-types';
import {
    Image,
    Modal,
    ModalHeader,
    ModalBody,
    ModalFooter,
    Button,
    Text,
    Box,
    Flex,
} from '@spothero/ui';
import useConfig from 'hooks/use-config';
import {useSelector} from 'react-redux';
import useMobileBreakPoint from 'hooks/use-mobile-breakpoint';

const CODE_VALUE = {
    QR_CODE: 'qrcode',
    CODE_128: 'code128',
    CODE_39: 'code39_nochecksum',
};

const ScanCode = ({rentalId, barcodeType, isActive}) => {
    const {siteUrl, isDeployed, spotHeroAPIHostInternal} = useConfig();
    const [modalOpen, setModalOpen] = useState(false);
    const [parkEarlySelected, setParkEarlySelected] = useState(false);
    const {accessKey} = useSelector(state => {
        return state.parkingPass;
    });
    const isDesktop = !useMobileBreakPoint('desktop');
    const basePath = !isDeployed ? spotHeroAPIHostInternal : siteUrl;
    const qrCodeSrc = accessKey
        ? `${basePath}/qrcode/${rentalId}?key=${accessKey}`
        : `${basePath}/qrcode/${rentalId}`;

    const toggleModalOpen = () => {
        setModalOpen(isOpen => !isOpen);
    };

    const handleParkEarlyClick = () => {
        setParkEarlySelected(true);
        toggleModalOpen();
    };

    const barcodeBlurred = !isActive && !parkEarlySelected && !isDesktop;
    const WrapperComponent = barcodeBlurred ? Button : Box;

    return (
        <>
            <WrapperComponent
                onClick={barcodeBlurred ? toggleModalOpen : null}
                aria-label={barcodeBlurred ? 'Show Barcode Early' : ''}
                background="transparent"
                boxShadow="none"
                padding={1}
                paddingTop={barcodeType === CODE_VALUE.QR_CODE ? 1 : 2.5}
                paddingBottom={barcodeType === CODE_VALUE.QR_CODE ? 1 : 0}
                border={0}
                borderRadius="md"
                _hover={{background: 'transparent'}}
                position="relative"
            >
                <Image
                    src={qrCodeSrc}
                    maxWidth={barcodeType === CODE_VALUE.QR_CODE ? 36 : 52}
                    // Add min height and width so less layout jump on load
                    minWidth={36}
                    minHeight={barcodeType === CODE_VALUE.QR_CODE ? 36 : 20}
                    data-testid="scan-code"
                    opacity={barcodeBlurred ? '0.25' : '1'}
                    filter={barcodeBlurred ? 'blur(2px)' : 'none'}
                />
                {barcodeBlurred && (
                    <Image
                        src="/img/parking-pass/too-early.svg"
                        width={6}
                        height="auto"
                        position="absolute"
                        right={0}
                        bottom={0}
                    />
                )}
            </WrapperComponent>
            <Modal
                hasHeader
                hasFooter
                size="sm"
                isOpen={modalOpen}
                onClose={toggleModalOpen}
            >
                <ModalHeader margin={0} as="h1" fontSize="lg">
                    Parking Early?
                </ModalHeader>
                <ModalBody>
                    <Flex width="100%" flexDirection="column">
                        <Text marginBottom={4} fontWeight="semibold">
                            You may be required to pay extra on-site when you
                            exit if you park now.
                        </Text>
                        <Text>
                            Wait until your reservation starts to avoid
                            additional costs.
                        </Text>
                    </Flex>
                </ModalBody>
                <ModalFooter>
                    <Button
                        fontFamily="body"
                        variant="secondary"
                        onClick={toggleModalOpen}
                        marginRight={4}
                    >
                        I&apos;ll Wait
                    </Button>
                    <Button
                        onClick={handleParkEarlyClick}
                        fontFamily="body"
                        variant="primary"
                    >
                        Park Early
                    </Button>
                </ModalFooter>
            </Modal>
        </>
    );
};

/* eslint-disable camelcase */
ScanCode.propTypes = {
    rentalId: PropTypes.number.isRequired,
    barcodeType: PropTypes.string.isRequired,
    isActive: PropTypes.bool.isRequired,
};

export default ScanCode;
